import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
  TextField
} from '@mui/material';
import { Security as SecurityIcon } from '@mui/icons-material';
import TwoFactorSetup from '../components/auth/TwoFactorSetup';
import apiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const ProfilePage: React.FC = () => {
  const [user, setUser] = useState<any>(null);
  const [twoFactorStatus, setTwoFactorStatus] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  const { user: authUser } = useAuth();

  // Editable fields
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);
  const [savingName, setSavingName] = useState(false);

  // Change password
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  const loadUserProfile = useCallback(async () => {
    try {
      const userData = await apiService.getCurrentUser();
      setUser(userData);
      setEmail(userData?.email || '');
      setDisplayName(userData?.display_name || '');
    } catch (err: any) {
      if (err.response?.status !== 404) {
        setError('Failed to load user profile');
      }
      setUser(authUser);
    }
  }, [authUser]);

  const loadTwoFactorStatus = useCallback(async () => {
    try {
      const status = await apiService.request('/api/auth/2fa/status');
      setTwoFactorStatus(status);
    } catch (err: any) {
      console.error('Failed to load 2FA status:', err);
      setTwoFactorStatus({ is_2fa_enabled: false });
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadUserProfile();
    loadTwoFactorStatus();
  }, [loadUserProfile, loadTwoFactorStatus]);

  const handleSaveEmail = async () => {
    setError('');
    setSuccess('');
    setSavingEmail(true);
    try {
      const updated = await apiService.updateMyProfile({ email });
      setUser(updated);
      setSuccess('Email updated');
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to update email');
    } finally {
      setSavingEmail(false);
    }
  };

  const handleSaveName = async () => {
    setError('');
    setSuccess('');
    setSavingName(true);
    try {
      const updated = await apiService.updateMyProfile({ display_name: displayName });
      setUser(updated);
      setSuccess('Name updated');
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to update name');
    } finally {
      setSavingName(false);
    }
  };

  const handleChangePassword = async () => {
    setError('');
    setSuccess('');
    if (!currentPassword || !newPassword) {
      setError('Please fill in current and new password');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match');
      return;
    }
    setChangingPassword(true);
    try {
      const needsTotp = twoFactorStatus?.is_2fa_enabled;
      await apiService.changeMyPassword(currentPassword, newPassword, needsTotp ? totpCode : undefined);
      setSuccess('Password changed');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTotpCode('');
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSetup2FA = () => {
    setSetupDialogOpen(true);
  };

  const handleSetupComplete = () => {
    loadTwoFactorStatus();
    setSetupDialogOpen(false);
  };

  const handleDisable2FA = async () => {
    if (window.confirm('Are you sure you want to disable two-factor authentication? This will make your account less secure.')) {
      alert('2FA disable feature requires additional verification. Please contact support.');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Box display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        Profile & Security
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Account Information
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1">
            <strong>Username:</strong> {user?.username || 'admin'}
          </Typography>
        </Box>
        <Box sx={{ mb: 2, maxWidth: 480 }}>
          <TextField
            fullWidth
            label="Name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            size="small"
          />
          <Box mt={1}>
            <Button variant="contained" onClick={handleSaveName} disabled={savingName}>
              {savingName ? 'Saving...' : 'Save Name'}
            </Button>
          </Box>
        </Box>
        <Box sx={{ mb: 2, maxWidth: 480 }}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            size="small"
          />
          <Box mt={1}>
            <Button variant="contained" onClick={handleSaveEmail} disabled={savingEmail}>
              {savingEmail ? 'Saving...' : 'Save Email'}
            </Button>
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Change Password
        </Typography>
        <Box sx={{ display: 'grid', gap: 2, maxWidth: 480 }}>
          <TextField
            label="Current Password"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            size="small"
            fullWidth
          />
          <TextField
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            size="small"
            fullWidth
          />
          <TextField
            label="Confirm New Password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            size="small"
            fullWidth
          />
          {twoFactorStatus?.is_2fa_enabled && (
            <TextField
              label="2FA Code"
              type="text"
              inputMode="numeric"
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value)}
              size="small"
              fullWidth
              placeholder="Enter 6-digit code"
            />
          )}
          <Box>
            <Button variant="contained" color="primary" onClick={handleChangePassword} disabled={changingPassword}>
              {changingPassword ? 'Changing...' : 'Change Password'}
            </Button>
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <SecurityIcon sx={{ mr: 1 }} />
          <Typography variant="h5">
            Two-Factor Authentication
          </Typography>
        </Box>

        <Typography variant="body1" color="text.secondary" paragraph>
          Add an extra layer of security to your account by requiring a verification code 
          from your authenticator app when signing in.
        </Typography>

        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box>
            <Typography variant="body1">
              Status: {twoFactorStatus?.is_2fa_enabled ? (
                <span style={{ color: 'green' }}>Enabled</span>
              ) : (
                <span style={{ color: 'orange' }}>Disabled</span>
              )}
            </Typography>
            {twoFactorStatus?.is_2fa_enabled && (
              <Typography variant="body2" color="text.secondary">
                Your account is protected with 2FA
              </Typography>
            )}
          </Box>

          <Box>
            {twoFactorStatus?.is_2fa_enabled ? (
              <Button
                variant="outlined"
                color="warning"
                onClick={handleDisable2FA}
              >
                Disable 2FA
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                onClick={handleSetup2FA}
              >
                Enable 2FA
              </Button>
            )}
          </Box>
        </Box>

        {!twoFactorStatus?.is_2fa_enabled && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              Your account is not protected by two-factor authentication. 
              We strongly recommend enabling 2FA to enhance your account security.
            </Typography>
          </Alert>
        )}
      </Paper>

      <TwoFactorSetup
        open={setupDialogOpen}
        onClose={() => setSetupDialogOpen(false)}
        onSuccess={handleSetupComplete}
      />
    </Container>
  );
};

export default ProfilePage;
